import React, { useState, useEffect } from 'react';
import { getMutuoBanca, saveMutuoBanca } from '../services/budgetMutuoBancaService';

const BudgetMutuoBancaTable = ({ cantiereId, onMutuoUpdated }) => {
  const formatEuro = (val) =>
    Number(val || 0).toLocaleString('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ' €';
  const [capitale, setCapitale] = useState('');
  const [interessi, setInteressi] = useState('');
  const [numTranche, setNumTranche] = useState(1);
  const [tranches, setTranches] = useState([{ richiesta_percent: '', mesi: '' }]);
  const [loading, setLoading] = useState(true);
  const [modificaAttiva, setModificaAttiva] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!cantiereId) return;
      try {
        const res = await getMutuoBanca(cantiereId);
        if (res.success && res.data) {
          setCapitale(res.data.capitale_richiesto);
          setInteressi(res.data.interessi_percent);
          setTranches(res.data.tranches);
          setNumTranche(res.data.tranches.length);
          setModificaAttiva(false);
        } else {
          // Nessun dato salvato: mostra direttamente il form
          setCapitale('');
          setInteressi('');
          setTranches([{ richiesta_percent: '', mesi: '' }]);
          setNumTranche(1);
          setModificaAttiva(true);
        }
      } catch (err) {
        console.error('Errore nel recupero mutuo banca:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [cantiereId]);

  const handleNumTrancheChange = (value) => {
    const n = parseInt(value) || 0;
    setNumTranche(n);
    setTranches(prev => {
      const copy = [...prev];
      if (n > copy.length) {
        for (let i = copy.length; i < n; i++) {
          copy.push({ richiesta_percent: '', mesi: '' });
        }
      } else {
        copy.length = n;
      }
      return copy;
    });
  };

  const handleTrancheChange = (index, field, value) => {
    setTranches(prev => prev.map((tr, i) => (i === index ? { ...tr, [field]: value } : tr)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dati = {
        capitale_richiesto: parseFloat(capitale) || 0,
        interessi_percent: parseFloat(interessi) || 0,
        tranches: tranches.map(t => ({
          richiesta_percent: parseFloat(t.richiesta_percent) || 0,
          mesi: parseInt(t.mesi) || 0,
        })),
      };
        await saveMutuoBanca(cantiereId, dati);
        const refreshed = await getMutuoBanca(cantiereId);
        if (refreshed.success && refreshed.data) {
          setCapitale(refreshed.data.capitale_richiesto);
          setInteressi(refreshed.data.interessi_percent);
          setTranches(refreshed.data.tranches);
          setNumTranche(refreshed.data.tranches.length);
        }
        setModificaAttiva(false);
        if (onMutuoUpdated) {
          await onMutuoUpdated();
        }
      } catch (error) {
        console.error('Errore salvataggio mutuo:', error);
        alert('Errore durante il salvataggio');
      }
    };

  const totaleRichiesta = tranches.reduce((sum, t) => sum + (parseFloat(t.richiesta_percent) || 0), 0);
  const capitalePerTranche = tranches.map(t => ((parseFloat(t.richiesta_percent) || 0) / 100) * (parseFloat(capitale) || 0));
  const totaleCapitale = capitalePerTranche.reduce((s, v) => s + v, 0);
  const interessiPerTranche = tranches.map(
    (t, i) =>
      ((capitalePerTranche[i] * ((parseFloat(interessi) || 0) / 100)) / 12) *
      (parseInt(t.mesi) || 0)
  );
  const totaleInteressi = interessiPerTranche.reduce((s, v) => s + v, 0);

  if (loading) {
    return <p>Caricamento mutuo banca...</p>;
  }

  if (modificaAttiva || tranches.length === 0) {
    return (
      <div className="card p-4">
        <form onSubmit={handleSubmit}>
          <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label">Capitale richiesto (€)</label>
              <input type="number" className="form-control" value={capitale} onChange={e => setCapitale(e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Interessi %</label>
              <input type="number" className="form-control" value={interessi} onChange={e => setInteressi(e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Numero tranche</label>
              <input type="number" className="form-control" value={numTranche} onChange={e => handleNumTrancheChange(e.target.value)} />
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th></th>
                  {tranches.map((_, idx) => (
                    <th key={idx}>Tranche {idx + 1}</th>
                  ))}
                  <th>Totale</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>Richiesta %</th>
                  {tranches.map((t, idx) => (
                    <td key={idx}>
                      <input type="number" className="form-control" value={t.richiesta_percent} onChange={e => handleTrancheChange(idx, 'richiesta_percent', e.target.value)} />
                    </td>
                  ))}
                  <td>{totaleRichiesta.toFixed(2)}</td>
                </tr>
                <tr className="table-light">
                  <th>Capitale</th>
                  {capitalePerTranche.map((val, idx) => (
                    <td key={idx}>{formatEuro(val)}</td>
                  ))}
                  <td>{formatEuro(totaleCapitale)}</td>
                </tr>
                <tr>
                  <th>Mesi</th>
                  {tranches.map((t, idx) => (
                    <td key={idx}>
                      <input type="number" className="form-control" value={t.mesi} onChange={e => handleTrancheChange(idx, 'mesi', e.target.value)} />
                    </td>
                  ))}
                  <td></td>
                </tr>
                <tr className="table-light">
                  <th>Interessi %</th>
                  {tranches.map((_, idx) => (
                    <td key={idx}>{interessi}</td>
                  ))}
                  <td></td>
                </tr>
                <tr>
                  <th>Interessi</th>
                  {interessiPerTranche.map((val, idx) => (
                    <td key={idx}>{formatEuro(val)}</td>
                  ))}
                  <td>{formatEuro(totaleInteressi)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <button type="submit" className="btn btn-primary mt-3">Salva</button>
        </form>
      </div>
    );
  }

  return (
    <div className="card p-4">
      <h5>Mutuo Banca</h5>
      <div className="mb-2"><strong>CAPITALE RICHIESTO:</strong> {formatEuro(capitale)}</div>
      <div className="mb-3"><strong>INTERESSI %:</strong> {interessi} %</div>
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th></th>
              {tranches.map((_, idx) => (
                <th key={idx}>Tranche {idx + 1}</th>
              ))}
              <th>Totale</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>Richiesta %</th>
              {tranches.map((t, idx) => (
                <td key={idx}>{parseFloat(t.richiesta_percent).toFixed(2)}</td>
              ))}
              <td>{totaleRichiesta.toFixed(2)}</td>
            </tr>
            <tr className="table-light">
              <th>Capitale</th>
              {capitalePerTranche.map((val, idx) => (
                <td key={idx}>{formatEuro(val)}</td>
              ))}
              <td>{formatEuro(totaleCapitale)}</td>
            </tr>
            <tr>
              <th>Mesi</th>
              {tranches.map((t, idx) => (
                <td key={idx}>{t.mesi}</td>
              ))}
              <td></td>
            </tr>
            <tr className="table-light">
              <th>Interessi %</th>
              {tranches.map((_, idx) => (
                <td key={idx}>{interessi}</td>
              ))}
              <td></td>
            </tr>
            <tr>
              <th>Interessi</th>
              {interessiPerTranche.map((val, idx) => (
                <td key={idx}>{formatEuro(val)}</td>
              ))}
              <td>{formatEuro(totaleInteressi)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="text-end mt-3">
        <button className="btn btn-warning" onClick={() => setModificaAttiva(true)}>
          ✏️ Modifica Dati
        </button>
      </div>
    </div>
  );
};

export default BudgetMutuoBancaTable;